/** Phase 4.2.0 — Agent Run Inspector.
 *
 * Loads GET /api/v1/agents/runs/{id} + /decisions.
 * Renders header (AIRunBadge-style chips), input, decisions, output, safety
 * verdict, provenance, and any linked artifacts (campaigns / landing pages /
 * assets / reports that reference this run as `source_agent_run_id`).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import QualityScoreCard from "../../components/app/QualityScoreCard";

function Json({ value, testid }) {
  return (
    <pre data-testid={testid}
      className="rounded bg-muted/30 p-3 overflow-auto text-[11px] max-h-[420px]">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function Chip({ label, value, mono = true, testid }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <Badge variant="outline" className={mono ? "font-mono text-[10px]" : "text-[10px]"}
      data-testid={testid}>
      <span className="text-muted-foreground mr-1">{label}:</span>{String(value)}
    </Badge>
  );
}

export default function AgentRunInspector() {
  const { id } = useParams();
  const t = useT();
  const [run, setRun] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [error, setError] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const r = await api.get(`/agents/runs/${id}`);
        if (!alive) return;
        setRun(r.data);
        try {
          const d = await api.get(`/agents/runs/${id}/decisions`);
          if (alive) setDecisions(d.data?.items || []);
        } catch (_e) { /* non-fatal */ }
      } catch (e) {
        if (alive) setError(e?.response?.status === 404
          ? t("agent_inspector.not_found")
          : t("agent_inspector.load_failed"));
      } finally {
        if (alive) setLoading(false);
      }
      // Best-effort artifact lookup runs AFTER the main fetch resolves the loading
      // state, so a slow / unsupported lookup endpoint can't stall the page.
      try {
        const ap = await Promise.allSettled([
          api.get("/campaigns", { params: { source_agent_run_id: id, limit: 5 },
                                   skipPermissionToast: true }),
          api.get("/landing-pages", { params: { source_agent_run_id: id, limit: 5 },
                                       skipPermissionToast: true }),
          api.get("/assets", { params: { source_agent_run_id: id, limit: 5 },
                                skipPermissionToast: true }),
          api.get("/autopilot/runs", { params: { optimization_agent_run_id: id, limit: 3 },
                                        skipPermissionToast: true }),
        ]);
        if (!alive) return;
        const found = [];
        const c = ap[0].status === "fulfilled" ? (ap[0].value.data.items || []) : [];
        const lp = ap[1].status === "fulfilled" ? (ap[1].value.data.items || []) : [];
        const as = ap[2].status === "fulfilled" ? (ap[2].value.data.items || []) : [];
        const ar = ap[3].status === "fulfilled" ? (ap[3].value.data.items || []) : [];
        c.filter((x) => x.source_agent_run_id === id).forEach((x) =>
          found.push({ kind: "campaign", id: x.id, name: x.name,
                        href: `/app/campaigns/${x.id}` }));
        lp.filter((x) => x.source_agent_run_id === id).forEach((x) =>
          found.push({ kind: "landing_page", id: x.id, name: x.framework,
                        href: `/app/landing-pages/${x.id}` }));
        as.filter((x) => x.source_agent_run_id === id).forEach((x) =>
          found.push({ kind: "asset", id: x.id, name: x.name,
                        href: `/app/assets/${x.id}` }));
        ar.filter((x) => x.optimization_agent_run_id === id).forEach((x) =>
          found.push({ kind: "autopilot_run", id: x.id, name: `Autopilot run · ${x.status}`,
                        href: `/app/autopilot/runs/${x.id}` }));
        setArtifacts(found);
      } catch (_e) { /* swallow */ }
    }
    load();
    return () => { alive = false; };
  }, [id, t]);

  if (loading) return <p className="text-sm text-muted-foreground"
    data-testid="agent-inspector-loading">{t("common.loading")}</p>;
  if (!run) return (
    <div data-testid="agent-inspector-error" className="text-sm">
      <p className="text-rose-600 mb-2">{error || t("agent_inspector.not_found")}</p>
      <Link to="/app/dashboard" className="underline text-xs">{t("common.back")}</Link>
    </div>
  );

  const safety = run.safety_verdict || run.output?.safety || null;

  return (
    <div data-testid="agent-inspector" className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("agent_inspector.title")}: {run.agent_key}
          </h1>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            run/{(run.id || "").slice(0, 12)} · v{run.version || 1} · {run.status}
          </p>
        </div>
        <Link to="/app/dashboard" className="text-xs underline text-muted-foreground"
          data-testid="agent-inspector-back">{t("common.back")}</Link>
      </div>

      <Card data-testid="agent-inspector-header-card">
        <CardContent className="pt-6 flex flex-wrap gap-2">
          <Chip label="agent" value={run.agent_key} testid="agent-chip-key" />
          <Chip label="version" value={run.agent_version || "1.0"} testid="agent-chip-version" />
          <Chip label="status" value={run.status} testid="agent-chip-status" />
          <Chip label="model" value={run.model} testid="agent-chip-model" />
          <Chip label="latency" value={run.latency_ms ? `${(run.latency_ms / 1000).toFixed(2)}s` : "—"}
                 testid="agent-chip-latency" />
          <Chip label="cost"
                 value={typeof run.cost_usd === "number" ? `$${run.cost_usd.toFixed(4)}` : "—"}
                 testid="agent-chip-cost" />
          <Chip label="correlation_id"
                 value={run.correlation_id ? run.correlation_id.slice(0, 12) : null}
                 testid="agent-chip-corr" />
        </CardContent>
      </Card>

      <Card data-testid="agent-inspector-input-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("agent_inspector.input")}</CardTitle></CardHeader>
        <CardContent><Json value={run.input || {}} testid="agent-inspector-input-json" /></CardContent>
      </Card>

      <Card data-testid="agent-inspector-decisions-card">
        <CardHeader className="pb-2 flex flex-row justify-between items-center">
          <CardTitle className="text-sm">{t("agent_inspector.decisions")}</CardTitle>
          <Badge variant="outline" className="font-mono text-[10px]"
            data-testid="agent-inspector-n-decisions">{decisions.length}</Badge>
        </CardHeader>
        <CardContent>
          {decisions.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="agent-inspector-no-decisions">
              {t("agent_inspector.no_decisions")}
            </p>
          ) : (
            <ul className="space-y-3">
              {decisions.map((d, i) => (
                <li key={d.id || i} data-testid={`agent-decision-${i}`}
                  className="border rounded-md p-3 bg-card/50 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs">{d.decision}</span>
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {(d.confidence ?? 0).toFixed(2)}
                    </Badge>
                  </div>
                  {d.reasoning && <p className="text-xs text-muted-foreground">{d.reasoning}</p>}
                  {(d.data_sources || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {d.data_sources.map((s, j) => (
                        <Badge key={j} variant="secondary" className="font-mono text-[9px]">{s}</Badge>
                      ))}
                    </div>
                  )}
                  {(d.alternatives_rejected || []).length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      <span className="font-medium">{t("agent_inspector.rejected")}:</span>{" "}
                      {(d.alternatives_rejected || []).join(", ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card data-testid="agent-inspector-output-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("agent_inspector.output")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {run.output?.summary && (
            <p className="text-sm" data-testid="agent-inspector-output-summary">
              {run.output.summary}
            </p>
          )}
          <Json value={run.output || {}} testid="agent-inspector-output-json" />
        </CardContent>
      </Card>

      <QualityScoreCard
        runId={run.id}
        score={run.quality_score_llm}
        onRescore={(fresh) => setRun({ ...run, quality_score_llm: fresh })}
      />

      {safety && (
        <Card data-testid="agent-inspector-safety-card">          <CardHeader className="pb-2"><CardTitle className="text-sm">{t("agent_inspector.safety")}</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1">
            <div>
              <Badge variant={safety.approved ? "outline" : "destructive"}
                data-testid="agent-inspector-safety-verdict">
                {safety.approved ? t("agent_inspector.safety_approved")
                                   : t("agent_inspector.safety_blocked")}
              </Badge>
              {typeof safety.score === "number" && (
                <span className="ml-2 text-muted-foreground">
                  score={safety.score.toFixed(2)}
                </span>
              )}
            </div>
            {(safety.violations || []).length > 0 && (
              <ul className="ml-3 list-disc">
                {safety.violations.map((v, i) =>
                  <li key={i} className="text-rose-600">{String(v)}</li>)}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <Card data-testid="agent-inspector-provenance-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("agent_inspector.provenance")}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
          <Chip label="prompt_key"
                 value={run.source_prompt_key || run.input?.prompt_key} testid="prov-prompt-key" />
          <Chip label="prompt_v"
                 value={run.source_prompt_version || run.input?.prompt_version}
                 testid="prov-prompt-version" />
          <Chip label="provider" value={run.source_llm_provider}
                 testid="prov-provider" />
          <Chip label="model" value={run.model} testid="prov-model" />
          <Chip label="cost_usd"
                 value={typeof run.cost_usd === "number" ? run.cost_usd.toFixed(4) : null}
                 testid="prov-cost" />
          <Chip label="latency_ms" value={run.latency_ms} testid="prov-latency" />
        </CardContent>
      </Card>

      <Card data-testid="agent-inspector-artifacts-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("agent_inspector.linked_artifacts")}</CardTitle></CardHeader>
        <CardContent>
          {artifacts.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="agent-inspector-no-artifacts">
              {t("agent_inspector.no_artifacts")}
            </p>
          ) : (
            <ul className="space-y-1 text-xs">
              {artifacts.map((a, i) => (
                <li key={i} data-testid={`agent-inspector-artifact-${i}`}>
                  <Badge variant="outline" className="font-mono text-[10px] mr-2">{a.kind}</Badge>
                  <Link to={a.href} className="underline">{a.name || a.id}</Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
